import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { Unit } from '@/utils/types';
import { pluralize } from '@/utils/utils';

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);
dayjs.extend(isBetween);

interface DateService {
	formatTime(date: Date): string;

	formatDate(date: Date): string;

	formatShortDateTime(date: Date): string;

	formatDateTime(date: Date): string;

	formatLongDateTime(date: Date, includeSeconds?: boolean): string;

	formatDifference(date1: Date, date2: Date): string;

	startOfDay(date: Date): Date;

	endOfDay(date: Date): Date;

	formatDifferenceFromNow(date: Date): string;

	add(date: Date, value: number, unit: Unit): Date;

	subtract(date: Date, value: number, unit: Unit): Date;

	isBefore(date1?: Date, date2?: Date, unit?: Unit): boolean;

	isSame(date1?: Date, date2?: Date, unit?: Unit): boolean;

	isAfter(date1?: Date, date2?: Date, unit?: Unit): boolean;

	difference(date1: Date, date2: Date, unit: Unit): number;

	utcMode(date: string, format: string): Date;

	formattedUtcMode(date: string, utcFormat?: string, format?: string): string;

	formatUtcCustom(date?: string | Date, format?: string): string;

	formatCustom(date?: string | Date, format?: string): string;

	formatDifferenceFromNowShort(date: Date): string;

	formatUtcDifferenceFromNowShort(date: Date): string;

	formatInTimezone(date: string | Date, format: string, timezone?: string): string;

	formatUTCInTimezone(date: string | Date | undefined | null, format: string, timezone?: string): string;

	isValid(date: string | Date): boolean;

	unix(date: Date): number;

	setUnixTime(date: Date): string;

	calculateYearsPassedFrom(date: Date): number;

	toISOStringWithOffset(date: Date): string;

	isBetween(startDate: Date, endDate: Date): boolean;

	formatMatchDuration(date1: Date, date2: Date): string;

	convertInTimezoneUTC(date: Date | string, tz: string): string;

	convertUTCToTimezone(date: string | Date, tz: string): Date;
}

class NativeDateService implements DateService {
	isBetween(startDate: Date, endDate: Date) {
		return dayjs().isBetween(startDate, endDate, 'hour', '[]');
	}
	format(date: Date, options: Intl.DateTimeFormatOptions): string {
		return date.toLocaleString('en-US', options);
	}

	formatTime(date: Date): string {
		return this.format(date, {
			hour: '2-digit',
			minute: '2-digit'
		});
	}

	formatDate(date: Date): string {
		return this.format(date, {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	}

	formatShortDateTime(date: Date): string {
		return this.format(date, {
			year: '2-digit',
			month: 'numeric',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	}

	formatDateTime(date: Date): string {
		return this.format(date, {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	}

	formatLongDateTime(date: Date, includeSeconds = true): string {
		const options: Intl.DateTimeFormatOptions = {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		};

		if (includeSeconds) {
			options.second = '2-digit';
		}

		return this.format(date, options);
	}

	formatDifference(date1: Date, date2: Date): string {
		let returnValue = '';
		let seconds = Math.abs(date2.getTime() - date1.getTime()) / 1000;

		const days = Math.floor(seconds / 86400);
		if (days > 0) {
			returnValue += `${days} ${pluralize(days, 'day')} `;
		}
		seconds -= days * 86400;

		const hours = Math.floor(seconds / 3600) % 24;
		if (hours > 0) {
			returnValue += `${hours} ${pluralize(hours, 'hour')} `;
		}
		seconds -= hours * 3600;

		const minutes = Math.floor(seconds / 60) % 60;
		if (minutes > 0) {
			returnValue += `${minutes} ${pluralize(minutes, 'min')}`;
		} else if (returnValue === '') {
			return 'A few seconds';
		}

		return returnValue.trimEnd();
	}

	formatDifferenceFromNow(date: Date): string {
		return this.formatDifference(date, new Date());
	}

	startOfDay(date: Date): Date {
		date.setHours(0, 0, 0, 0);
		return date;
	}

	endOfDay(date: Date): Date {
		date.setHours(23, 59, 59, 999);
		return date;
	}

	calculateYearsPassedFrom(date: Date): number {
		return dayjs().diff(date, 'years');
	}

	add(_date: Date, _value: number, _unit: Unit): Date {
		throw new Error('add is not supported in the native date service implementation.');
	}

	subtract(_date: Date, _value: number, _unit: Unit): Date {
		throw new Error('subtract is not supported in the native date service implementation.');
	}

	isBefore(_date1: Date, _date2: Date, _unit: Unit): boolean {
		throw new Error('isBefore is not supported in the native date service implementation.');
	}

	isSame(_date1: Date, _date2: Date, _unit: Unit): boolean {
		throw new Error('isSame is not supported in the native date service implementation.');
	}

	isAfter(_date1: Date, _date2: Date, _unit: Unit): boolean {
		throw new Error('isAfter is not supported in the native date service implementation.');
	}

	difference(_date1: Date, _date2: Date, _unit: Unit): number {
		throw new Error('difference is not supported in the native date service implementation.');
	}

	utcMode(_date: string, _format: string): Date {
		throw new Error('utcMode is not supported in the native date service implementation.');
	}

	formattedUtcMode(_date: string, _utcFormat: string, _format: string): string {
		throw new Error('formattedUtcMode is not supported in the native date service implementation.');
	}

	formatUtcCustom(_date: string, _format: string): string {
		throw new Error('formatUtcCustom is not supported in the native date service implementation.');
	}

	formatCustom(_date: string | Date, _format: string): string {
		throw new Error('formatCustom is not supported in the native date service implementation.');
	}

	formatDifferenceFromNowShort(_date: Date): string {
		throw new Error('formatDifferenceFromNowShort is not supported in the native date service implementation.');
	}

	formatUtcDifferenceFromNowShort(_date: Date): string {
		throw new Error('formatUtcDifferenceFromNowShort is not supported in the native date service implementation.');
	}

	formatInTimezone(_date: string, _format: string, _timezone: string): string {
		throw new Error('formatInTimezone is not supported in the native date service implementation.');
	}

	formatUTCInTimezone(date: string | Date | undefined | null, format: string, timezone?: string): string {
		if (!date) return '';
		return dayjs
			.utc(date)
			.tz(timezone || 'UTC')
			.format(format);
	}

	isValid(_date: string | Date): boolean {
		throw new Error('isValid is not supported in the native date service implementation.');
	}

	unix(_date: Date): number {
		throw new Error('unix is not supported in the native date service implementation.');
	}
	setUnixTime(date: Date) {
		const unixStartDate = Math.floor(date?.getTime() / 1000);
		return unixStartDate.toString();
	}
	formatMatchDuration(_date1: Date, _date2: Date): string {
		throw new Error('formatMatchDuration is not supported in the native date service implementation.');
	}

	convertInTimezoneUTC(_date: Date | string, _tz: string): string {
		throw new Error('formatInTimezoneUTC is not supported in the native date service implementation.');
	}

	convertUTCToTimezone(_date: string | Date, _tz: string): Date {
		throw new Error('convertUTCToTimezone is not supported in the native date service implementation.');
	}

	toISOStringWithOffset(date: Date) {
		const pad = (num: number) => num.toString().padStart(2, '0');

		// Get the timezone offset in minutes
		const timezoneOffset = date.getTimezoneOffset(); // e.g., -120 for UTC+2

		// Calculate offset hours and minutes
		const absOffset = Math.abs(timezoneOffset);
		const offsetHours = Math.floor(absOffset / 60);
		const offsetMinutes = absOffset % 60;

		// Format the offset as ±HH:mm
		const offsetSign = timezoneOffset > 0 ? '-' : '+';
		const offset = `${offsetSign}${pad(offsetHours)}:${pad(offsetMinutes)}`;

		// Get ISO components in local time
		const year = date.getFullYear();
		const month = pad(date.getMonth() + 1); // Month is 0-based
		const day = pad(date.getDate());
		const hours = pad(date.getHours());
		const minutes = pad(date.getMinutes());
		const seconds = pad(date.getSeconds());
		const milliseconds = date.getMilliseconds().toString().padStart(3, '0');

		return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${offset}`;
	}
}

class DayjsDateService extends NativeDateService {
	add(date: Date, value: number, unit: Unit): Date {
		return dayjs(date).add(value, unit).toDate();
	}

	subtract(date: Date, value: number, unit: Unit): Date {
		return dayjs(date).subtract(value, unit).toDate();
	}

	isBefore(date1: Date, date2: Date, unit: Unit): boolean {
		return dayjs(date1).isBefore(date2, unit);
	}

	isSame(date1: Date, date2: Date, unit: Unit): boolean {
		return dayjs(date1).isSame(date2, unit);
	}

	isAfter(date1: Date, date2: Date, unit: Unit): boolean {
		return dayjs(date1).isAfter(date2, unit);
	}

	difference(date1: Date, date2: Date, unit: Unit): number {
		return dayjs(date1).diff(date2, unit);
	}

	utcMode(date: string, utcFormat: string = 'H:mm'): Date {
		return dayjs.utc(date, utcFormat).toDate();
	}

	formattedUtcMode(date: string = '0:00', utcFormat: string = 'H:mm', format: string = 'h:mm A'): string {
		return dayjs.utc(date, utcFormat).format(format);
	}

	formatUtcCustom(date: string | Date, format: string = 'MMM D YYYY h:mm:ss A', timeZone?: string): string {
		if (!date) return '';

		if (timeZone) {
			const dt = dayjs.utc(date).tz(timeZone);
			return dt.format(format);
		} else {
			return dayjs.utc(date).format(format);
		}
	}

	formatCustom(date: string | Date, format: string = 'MMM D YYYY h:mm:ss A'): string {
		return dayjs(date).format(format);
	}

	formatDifferenceFromNowShort(date: Date): string {
		return dayjs(date).fromNow();
	}

	formatUtcDifferenceFromNowShort(date: Date): string {
		return dayjs.utc(date).fromNow();
	}

	formatInTimezone(date: string, format: string, timezone: string = 'America/New_York'): string {
		return dayjs(date).tz(timezone).format(format);
	}

	isValid(date: string | Date): boolean {
		return dayjs(date).isValid();
	}

	unix(date: Date): number {
		return dayjs(date).unix();
	}

	formatMatchDuration(date1: Date, date2: Date): string {
		const start = dayjs(date2);
		const end = dayjs(date1);

		const totalMinutes = end.diff(start, 'minute');
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;

		if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
		if (hours > 0) return `${hours}h`;
		if (minutes > 0) return `${minutes}m`;
		return '0m';
	}

	convertInTimezoneUTC(date: Date, tz: string): string {
		const year = date.getFullYear();
		const month = date.getMonth() + 1;
		const day = date.getDate();
		const hour = date.getHours();
		const minute = date.getMinutes();
		const second = date.getSeconds();

		const dateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
		return dayjs.tz(dateString, tz).utc().format();
	}

	convertUTCToTimezone(date: string | Date, tz: string): Date {
		const utcTime = dayjs.utc(date);
		const detroitTime = utcTime.tz(tz);

		const localDate = new Date(
			detroitTime.year(),
			detroitTime.month(),
			detroitTime.date(),
			detroitTime.hour(),
			detroitTime.minute(),
			detroitTime.second()
		);
		return localDate;
	}
}

export const DateUtil: DateService = new DayjsDateService();

export const formatShortDateRange = (start: string, end: string, isMonthView = false): string => {
	const startDate = dayjs(start);
	const endDate = dayjs(end);

	const startFormat = isMonthView ? 'MMMM D' : 'MMM D';
	const endFormatSameMonth = 'D';
	const endFormatDifferentMonth = isMonthView ? 'MMMM D' : 'MMM D';

	if (startDate.month() === endDate.month()) {
		return `${startDate.format(startFormat)}–${endDate.format(endFormatSameMonth)}`;
	} else {
		return `${startDate.format(startFormat)} – ${endDate.format(endFormatDifferentMonth)}`;
	}
};

export const formatDateRangeWithYear = (start: string, end: string): string => {
	const startDate = dayjs(start);
	const endDate = dayjs(end);

	const sameMonth = startDate.month() === endDate.month();
	const sameYear = startDate.year() === endDate.year();

	if (sameMonth && sameYear) {
		return `${startDate.format('MMM D')}–${endDate.format('D YYYY')}`;
	} else if (sameYear) {
		return `${startDate.format('MMM D')} – ${endDate.format('MMM D YYYY')}`;
	} else {
		return `${startDate.format('MMM D YYYY')} – ${endDate.format('MMM D YYYY')}`;
	}
};
